import React, { useEffect, useState, useRef } from 'react';
import { Table, Button, Modal, Input, notification, Popconfirm, Select, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import Loader from '../atoms/Loader';

const axios = require('axios').default;

const PageHead = styled.div`
  display: flex;
  height: 70px;
  justify-content: space-between;
  align-items: center;
  padding: 5px 20px;
`;

const StyledLoad = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const SearchRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const ManagerStudents = () => {
  const [managers, setManagers] = useState([]);
  const [managerSearch, setManagerSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // Assign modal
  const [assignModal, setAssignModal] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [managerEmail, setManagerEmail] = useState('');
  const [studentsEmails, setStudentsEmails] = useState('');

  // Students modal
  const [studentsModal, setStudentsModal] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [studentList, setStudentList] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);

  // Bulk student upload (Excel)
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Student filters
  const [searchName, setSearchName] = useState('');
  const [searchSchool, setSearchSchool] = useState('');
  const [searchLevel, setSearchLevel] = useState('');
  const [searchBranch, setSearchBranch] = useState('');

  // Edit student school/level/branch modal
  const [editModal, setEditModal] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [editSchool, setEditSchool] = useState('');
  const [editLevel, setEditLevel] = useState('');
  const [editBranch, setEditBranch] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // Edit student account modal (email, login_type, password)
  const [accountModal, setAccountModal] = useState(false);
  const [accountStudent, setAccountStudent] = useState(null);
  const [accountEmail, setAccountEmail] = useState('');
  const [accountLoginType, setAccountLoginType] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [accountLoading, setAccountLoading] = useState(false);

  // Add student search
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addStudentLoading, setAddStudentLoading] = useState(false);
  const searchTimeout = useRef(null);

  const getManagers = async () => {
    setLoading(true);
    try {
      const res = await axios.get('manager/list');
      setManagers(res.data.list);
    } catch (err) {
      notification.error({ message: 'Could not load manager list' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getManagers();
  }, []);

  const openStudents = async (record) => {
    setSelectedManager(record);
    setStudentsModal(true);
    setStudentsLoading(true);
    setSearchName('');
    setSearchSchool('');
    setSearchLevel('');
    setSearchBranch('');
    try {
      const res = await axios.get(`manager/${record.id}/students`);
      setStudentList(res.data.list);
    } catch (err) {
      notification.error({ message: 'Could not load student list' });
    } finally {
      setStudentsLoading(false);
    }
  };

  const openBulkModal = () => {
    setBulkResult(null);
    setBulkModal(true);
  };

  const handleBulkUpload = async (file) => {
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post('manager/students/bulk', formData);
      setBulkResult(res.data);
      const { created = 0, skippedExisting = [], invalid = [] } = res.data;
      notification.success({
        message: `${created} student account(s) created`,
        description: `Skipped: ${skippedExisting.length} · Invalid: ${invalid.length}`,
      });
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Upload failed' });
    } finally {
      setBulkLoading(false);
    }
  };

  const openEditModal = (record) => {
    setEditStudent(record);
    setEditSchool(record.school_name || '');
    setEditLevel(record.level != null ? String(record.level) : '');
    setEditBranch(record.branch || '');
    setEditModal(true);
  };

  const handleEditSave = async () => {
    setEditLoading(true);
    try {
      await axios.post(`manager/student/${editStudent.id}/info`, {
        school_name: editSchool,
        level: editLevel,
        branch: editBranch,
      });
      notification.success({ message: 'Details updated' });
      setStudentList((prev) =>
        prev.map((s) =>
          s.id === editStudent.id
            ? { ...s, school_name: editSchool, level: editLevel, branch: editBranch }
            : s
        )
      );
      setEditModal(false);
    } catch (err) {
      notification.error({ message: 'Something went wrong' });
    } finally {
      setEditLoading(false);
    }
  };

  const openAccountModal = (record) => {
    setAccountStudent(record);
    setAccountEmail(record.email || '');
    setAccountLoginType(record.login_type || '');
    setAccountPassword('');
    setAccountModal(true);
  };

  const handleAccountSave = async () => {
    setAccountLoading(true);
    try {
      await axios.post(`manager/student/${accountStudent.id}/account`, {
        email: accountEmail,
        login_type: accountLoginType,
        password: accountPassword,
      });
      notification.success({ message: 'Account updated' });
      setStudentList((prev) =>
        prev.map((s) =>
          s.id === accountStudent.id
            ? { ...s, email: accountEmail, login_type: accountLoginType }
            : s
        )
      );
      setAccountModal(false);
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Something went wrong' });
    } finally {
      setAccountLoading(false);
    }
  };

  const handleRemoveStudent = async (student_id) => {
    try {
      await axios.delete(`manager/${selectedManager.id}/student/${student_id}`);
      notification.success({ message: 'Student removed' });
      setStudentList((prev) => prev.filter((s) => s.id !== student_id));
    } catch (err) {
      notification.error({ message: 'Something went wrong' });
    }
  };

  const handleStudentSearch = (value) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!value || value.length < 3) {
      setSearchResults([]);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get(`manager/students/search?q=${value}`);
        setSearchResults(res.data.list.map((s) => ({
          value: s.id,
          label: `${s.first_name} ${s.last_name} — ${s.email}`,
          ...s,
        })));
      } catch (err) {
        console.error(err);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
  };

  const handleAddStudent = async (student) => {
    setAddStudentLoading(true);
    try {
      await axios.post('manager/student/add', {
        manager_id: selectedManager.id,
        student_email: student.email,
      });
      notification.success({ message: 'Student added' });
      const res = await axios.get(`manager/${selectedManager.id}/students`);
      setStudentList(res.data.list);
      setSearchResults([]);
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Something went wrong' });
    } finally {
      setAddStudentLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!managerEmail.trim()) {
      notification.warning({ message: 'Enter manager email' });
      return;
    }
    if (!studentsEmails.trim()) {
      notification.warning({ message: 'Enter at least one student email' });
      return;
    }
    const studentsArray = studentsEmails.split(',').map((e) => e.trim()).filter(Boolean);
    setAssignLoading(true);
    try {
      const res = await axios.post('manager/assign', {
        manager_email: managerEmail.trim(),
        students: studentsArray,
      });
      const { assigned, notFound, alreadyAssigned } = res.data;
      notification.success({ message: `${assigned} student(s) assigned` });
      if (notFound && notFound.length > 0) {
        notification.warning({ message: 'Users not found', description: notFound.join(', '), duration: 8 });
      }
      if (alreadyAssigned && alreadyAssigned.length > 0) {
        notification.info({ message: 'Already assigned', description: alreadyAssigned.join(', '), duration: 6 });
      }
      setAssignModal(false);
      setManagerEmail('');
      setStudentsEmails('');
      getManagers();
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Something went wrong' });
    } finally {
      setAssignLoading(false);
    }
  };

  const filteredManagers = managers.filter((m) => {
    const q = managerSearch.toLowerCase();
    return (
      !q ||
      m.first_name?.toLowerCase().includes(q) ||
      m.last_name?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q)
    );
  });

  const filteredStudents = studentList.filter((s) => {
    const qName = searchName.toLowerCase();
    const qSchool = searchSchool.toLowerCase();
    const qLevel = searchLevel.toLowerCase();
    const qBranch = searchBranch.toLowerCase();
    return (
      (!qName || s.first_name?.toLowerCase().includes(qName) || s.last_name?.toLowerCase().includes(qName) || s.email?.toLowerCase().includes(qName)) &&
      (!qSchool || s.school_name?.toLowerCase().includes(qSchool)) &&
      (!qLevel || String(s.level ?? '').toLowerCase().includes(qLevel)) &&
      (!qBranch || s.branch?.toLowerCase().includes(qBranch))
    );
  });

  const managerColumns = [
    { title: 'First name', dataIndex: 'first_name', key: 'first_name' },
    { title: 'Last name', dataIndex: 'last_name', key: 'last_name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <Button type="primary" size="small" onClick={() => openStudents(record)}>
          Students
        </Button>
      ),
    },
  ];

  const studentColumns = [
    { title: 'First name', dataIndex: 'first_name', key: 'first_name' },
    { title: 'Last name', dataIndex: 'last_name', key: 'last_name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'School', dataIndex: 'school_name', key: 'school_name' },
    { title: 'Level', dataIndex: 'level', key: 'level' },
    { title: 'Branch', dataIndex: 'branch', key: 'branch' },
    { title: 'Login type', dataIndex: 'login_type', key: 'login_type' },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {/* Edit işlevi geçici olarak pasif — daha sonra ele alınacak */}
          <Button size="small" onClick={() => openEditModal(record)} disabled>Edit</Button>
          <Button size="small" onClick={() => openAccountModal(record)}>Account</Button>
          <Popconfirm
            title="Remove this student from the list?"
            onConfirm={() => handleRemoveStudent(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger size="small">Delete</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const isGoogleAccount = accountStudent?.login_type === 'google';

  return (
    <>
      <PageHead>
        <h1 className="h3 mt-3"><strong>MANAGER</strong> AND STUDENTS</h1>
        <Button type="primary" onClick={() => setAssignModal(true)}>
          Manager and Students Add
        </Button>
      </PageHead>

      <div style={{ padding: '0 20px 16px' }}>
        <Input.Search
          placeholder="Search by name or email..."
          allowClear
          value={managerSearch}
          onChange={(e) => setManagerSearch(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      </div>

      {loading ? (
        <StyledLoad><Loader size="md" /></StyledLoad>
      ) : (
        <Table columns={managerColumns} dataSource={filteredManagers} rowKey="id" />
      )}

      {/* Manager + Students Assign Modal */}
      <Modal
        title="Manager and Students Add"
        open={assignModal}
        onOk={handleAssign}
        onCancel={() => { setAssignModal(false); setManagerEmail(''); setStudentsEmails(''); }}
        confirmLoading={assignLoading}
        okText="Save"
        cancelText="Cancel"
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Manager email</label>
          <Input
            placeholder="manager@example.com"
            value={managerEmail}
            onChange={(e) => setManagerEmail(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Student emails</label>
          <Input.TextArea
            rows={4}
            placeholder="student1@example.com, student2@example.com"
            value={studentsEmails}
            onChange={(e) => setStudentsEmails(e.target.value)}
          />
          <small style={{ color: '#999' }}>Separate multiple students with commas</small>
        </div>
      </Modal>

      {/* Students Modal */}
      <Modal
        title={selectedManager ? `${selectedManager.first_name} ${selectedManager.last_name} — Students` : 'Students'}
        open={studentsModal}
        onCancel={() => { setStudentsModal(false); setSelectedManager(null); setStudentList([]); setSearchResults([]); }}
        footer={null}
        width={1100}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Button icon={<UploadOutlined />} onClick={openBulkModal}>
            Bulk add students (Excel)
          </Button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Add student — type at least 3 characters"
            filterOption={false}
            onSearch={handleStudentSearch}
            loading={searchLoading}
            options={searchResults}
            onSelect={(_, option) => handleAddStudent(option)}
            value={null}
            notFoundContent={searchLoading ? 'Searching...' : 'No results'}
            disabled={addStudentLoading}
          />
        </div>

        <SearchRow>
          <Input
            placeholder="Name or email"
            allowClear
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            style={{ flex: 2, minWidth: 160 }}
          />
          <Input
            placeholder="School"
            allowClear
            value={searchSchool}
            onChange={(e) => setSearchSchool(e.target.value)}
            style={{ flex: 2, minWidth: 140 }}
          />
          <Input
            placeholder="Level"
            allowClear
            value={searchLevel}
            onChange={(e) => setSearchLevel(e.target.value)}
            style={{ flex: 1, minWidth: 80 }}
          />
          <Input
            placeholder="Branch"
            allowClear
            value={searchBranch}
            onChange={(e) => setSearchBranch(e.target.value)}
            style={{ flex: 1, minWidth: 80 }}
          />
        </SearchRow>

        {studentsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Loader size="md" /></div>
        ) : (
          <Table columns={studentColumns} dataSource={filteredStudents} rowKey="id" size="small" />
        )}
      </Modal>

      {/* Bulk student upload (Excel) */}
      <Modal
        title="Bulk add students (Excel)"
        open={bulkModal}
        onCancel={() => setBulkModal(false)}
        footer={null}
        width={560}
      >
        <p style={{ marginBottom: 8 }}>
          Upload an <strong>.xlsx</strong> / <strong>.xls</strong> / <strong>.csv</strong> file with these
          columns in the first row:
        </p>
        <p style={{ marginBottom: 16 }}>
          <code>Adı</code> &nbsp;·&nbsp; <code>Soyadı</code> &nbsp;·&nbsp; <code>Mail</code> &nbsp;·&nbsp;{' '}
          <code>Şifre</code>
        </p>

        <Upload
          accept=".xlsx,.xls,.csv"
          maxCount={1}
          showUploadList={false}
          beforeUpload={(file) => { handleBulkUpload(file); return false; }}
          disabled={bulkLoading}
        >
          <Button icon={<UploadOutlined />} loading={bulkLoading}>
            {bulkLoading ? 'Uploading...' : 'Select file'}
          </Button>
        </Upload>

        {bulkResult && (
          <div style={{ marginTop: 20 }}>
            <p style={{ marginBottom: 4 }}>
              <strong>{bulkResult.created}</strong> created ·{' '}
              <strong>{(bulkResult.skippedExisting || []).length}</strong> skipped ·{' '}
              <strong>{(bulkResult.invalid || []).length}</strong> invalid
              &nbsp;(of {bulkResult.total} rows)
            </p>

            {(bulkResult.skippedExisting || []).length > 0 && (
              <>
                <p style={{ margin: '12px 0 4px', fontWeight: 500 }}>Skipped</p>
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  {bulkResult.skippedExisting.map((s, i) => (
                    <li key={`sk-${i}`}>
                      Row {s.row}: {s.email} — {s.reason}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {(bulkResult.invalid || []).length > 0 && (
              <>
                <p style={{ margin: '12px 0 4px', fontWeight: 500 }}>Invalid</p>
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  {bulkResult.invalid.map((s, i) => (
                    <li key={`iv-${i}`}>
                      Row {s.row}: {s.email || '(no email)'} — {s.reason}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Edit student info (school / level / branch) */}
      <Modal
        title={editStudent ? `${editStudent.first_name} ${editStudent.last_name} — Edit` : 'Edit'}
        open={editModal}
        onOk={handleEditSave}
        onCancel={() => setEditModal(false)}
        confirmLoading={editLoading}
        okText="Save"
        cancelText="Cancel"
        width={420}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>School</label>
            <Input
              placeholder="School name"
              value={editSchool}
              onChange={(e) => setEditSchool(e.target.value)}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Class (level)</label>
            <Input
              placeholder="Class / level"
              value={editLevel}
              onChange={(e) => setEditLevel(e.target.value)}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Branch</label>
            <Input
              placeholder="Branch"
              value={editBranch}
              onChange={(e) => setEditBranch(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      {/* Edit student account (email, login type, password) */}
      <Modal
        title={accountStudent ? `${accountStudent.first_name} ${accountStudent.last_name} — Edit account` : 'Edit account'}
        open={accountModal}
        onOk={handleAccountSave}
        onCancel={() => setAccountModal(false)}
        confirmLoading={accountLoading}
        okText="Save"
        cancelText="Cancel"
        width={420}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Email</label>
            <Input
              placeholder="Email address"
              value={accountEmail}
              onChange={(e) => setAccountEmail(e.target.value)}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Login type</label>
            <Select
              style={{ width: '100%' }}
              value={accountLoginType}
              onChange={(val) => { setAccountLoginType(val); setAccountPassword(''); }}
              options={[
                { value: 'google', label: 'Google', disabled: !isGoogleAccount },
                { value: 'normal', label: 'Email & password' },
              ]}
              disabled={!isGoogleAccount}
            />
            {!isGoogleAccount && (
              <small style={{ color: '#999' }}>Email accounts cannot be switched to Google</small>
            )}
          </div>
          {accountLoginType === 'normal' && isGoogleAccount && (
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>New password</label>
              <Input.Password
                placeholder="At least 6 characters"
                value={accountPassword}
                onChange={(e) => setAccountPassword(e.target.value)}
              />
              <small style={{ color: '#999' }}>Set a password to switch from Google to email login</small>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ManagerStudents;
